import {
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-model/ids";
import { ReturnStatusZod } from "@redotech/redo-model/return-status";
import {
  ReturnsSortingMode,
  prioritySort,
  serviceLevelSort,
  sortingReason,
} from "@redotech/redo-model/returns/sorting-types";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";

// Return processing flow step schemas
export const ReturnProcessingStepScope = {
  RETURN: "return",
  RETURN_ITEM: "return_item",
} as const;
export type ReturnProcessingStepScope =
  (typeof ReturnProcessingStepScope)[keyof typeof ReturnProcessingStepScope];
const returnProcessingStepScopeValues = Object.values(
  ReturnProcessingStepScope,
) as [ReturnProcessingStepScope, ...ReturnProcessingStepScope[]];

export const ReturnProcessingStepType = {
  DECISION: "return_processing_decision",
  INFORMATION: "return_processing_information",
  LICENSE_PLATE: "license_plate",
  SORT: "sort",
  EVIDENCE: "evidence",
  ADD_UNDECLARED_ITEM: "add_undeclared_item",
  VERIFICATION: "verification",
} as const;
export type ReturnProcessingStepType =
  (typeof ReturnProcessingStepType)[keyof typeof ReturnProcessingStepType];

const ReturnProcessingDecisionStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("return_processing_decision"),
  prompt: z.string(),
  decisionKey: z.string(),
  trueLabel: z.string(),
  falseLabel: z.string(),
  nextTrueId: z.number().nullable(),
  nextFalseId: z.number().nullable(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const ReturnProcessingDecisionStep: z.ZodType<ReturnProcessingDecisionStep> =
  ReturnProcessingDecisionStep_;
export interface ReturnProcessingDecisionStep
  extends z.infer<typeof ReturnProcessingDecisionStep_> {}

const ReturnProcessingInformationStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("return_processing_information"),
  header: z.string(),
  message: z.string(),
  continueButtonText: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const ReturnProcessingInformationStep: z.ZodType<ReturnProcessingInformationStep> =
  ReturnProcessingInformationStep_;
export interface ReturnProcessingInformationStep
  extends z.infer<typeof ReturnProcessingInformationStep_> {}

const LicensePlateStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("license_plate"),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const LicensePlateStep: z.ZodType<LicensePlateStep> = LicensePlateStep_;
export interface LicensePlateStep extends z.infer<typeof LicensePlateStep_> {}

const SortStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("sort"),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  destination: z.string(),
  customTitle: z.string().optional(),
});
export const SortStep: z.ZodType<SortStep> = SortStep_;
export interface SortStep extends z.infer<typeof SortStep_> {}

const EvidenceStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("evidence"),
  prompt: z.string(),
  notesLabel: z.string().optional(),
  notesPlaceholder: z.string().optional(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const EvidenceStep: z.ZodType<EvidenceStep> = EvidenceStep_;
export interface EvidenceStep extends z.infer<typeof EvidenceStep_> {}

const AddUndeclaredItemStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("add_undeclared_item"),
  prompt: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const AddUndeclaredItemStep: z.ZodType<AddUndeclaredItemStep> =
  AddUndeclaredItemStep_;
export interface AddUndeclaredItemStep
  extends z.infer<typeof AddUndeclaredItemStep_> {}

const VerificationStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("verification"),
  prompt: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const VerificationStep: z.ZodType<VerificationStep> = VerificationStep_;
export interface VerificationStep extends z.infer<typeof VerificationStep_> {}

const ReturnProcessingStep_ = z.discriminatedUnion("type", [
  ReturnProcessingDecisionStep_,
  ReturnProcessingInformationStep_,
  LicensePlateStep_,
  SortStep_,
  EvidenceStep_,
  AddUndeclaredItemStep_,
  VerificationStep_,
]);
export const ReturnProcessingStep: z.ZodType<ReturnProcessingStep> =
  ReturnProcessingStep_;
export type ReturnProcessingStep = z.infer<typeof ReturnProcessingStep_>;

const ReturnProcessingFlow_ = z.object({
  steps: z.array(ReturnProcessingStep_),
});
export const ReturnProcessingFlow: z.ZodType<ReturnProcessingFlow> =
  ReturnProcessingFlow_;
export interface ReturnProcessingFlow
  extends z.infer<typeof ReturnProcessingFlow_> {}

const ProcessingReturnItem_ = z.object({
  id: zExt.objectId(),
  productTitle: z.string(),
  variantTitle: z.string(),
  sku: z.string().nullish(),
  barcode: z.string().nullish(),
  quantity: z.number().int().positive(),
  images: z.array(z.string().nullable()),
  reason: z.string().nullish(),
  price: z.string(),
  currencyCode: z.string().nullish(),
  vendor: z.string().nullish(),
  description: z.string().nullish(),
  customerNotes: z.string().nullish(),
  customerImages: z.array(z.string()),
});
export const ProcessingReturnItem: z.ZodType<ProcessingReturnItem> =
  ProcessingReturnItem_;
export interface ProcessingReturnItem
  extends z.infer<typeof ProcessingReturnItem_> {}

const DispositionSettings_ = z.object({
  enabled: z.boolean(),
  gradeOptions: z.array(z.string()),
  outcomeOptions: z.array(z.string()),
});
export const DispositionSettings: z.ZodType<DispositionSettings> =
  DispositionSettings_;
export interface DispositionSettings
  extends z.infer<typeof DispositionSettings_> {}

const ReturnsSortingModeSchema_ = z.enum(ReturnsSortingMode);
export const ReturnsSortingModeSchema: z.ZodType<ReturnsSortingModeSchema> =
  ReturnsSortingModeSchema_;
export type ReturnsSortingModeSchema = z.infer<
  typeof ReturnsSortingModeSchema_
>;

const ServiceLevelSortSchema_ = z.enum(serviceLevelSort);
export const ServiceLevelSortSchema: z.ZodType<ServiceLevelSortSchema> =
  ServiceLevelSortSchema_;
export type ServiceLevelSortSchema = z.infer<typeof ServiceLevelSortSchema_>;

const PrioritySortSchema_ = z.enum(prioritySort);
export const PrioritySortSchema: z.ZodType<PrioritySortSchema> =
  PrioritySortSchema_;
export type PrioritySortSchema = z.infer<typeof PrioritySortSchema_>;

const ActiveSortSchema_ = z.union([
  ServiceLevelSortSchema_,
  PrioritySortSchema_,
]);

const SortingReasonSchema_ = z.enum(sortingReason);

const SortingInfo_ = z.object({
  sortingMode: ReturnsSortingModeSchema_,
  serviceLevel: ServiceLevelSortSchema_,
  priority: PrioritySortSchema_,
  activeSort: ActiveSortSchema_,
  destination: z.string(),
  reasons: z.array(SortingReasonSchema_),
  sortingFlow: ReturnProcessingFlow_,
});
export const SortingInfo: z.ZodType<SortingInfo> = SortingInfo_;
export interface SortingInfo extends z.infer<typeof SortingInfo_> {}

const ProcessingReturnData_ = z.object({
  id: zExt.objectId(),
  orderNumber: z.string(),
  status: ReturnStatusZod,
  items: z.array(ProcessingReturnItem_),
  dispositionSettings: DispositionSettings_.nullable(),
  returnProcessingFlow: ReturnProcessingFlow_,
  sortingInfo: SortingInfo_,
  createdAt: z.date(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  processingAgentEvalEnabled: z.boolean(),
});
export const ProcessingReturnData: z.ZodType<ProcessingReturnData> =
  ProcessingReturnData_;
export interface ProcessingReturnData
  extends z.infer<typeof ProcessingReturnData_> {}

export const getReturnForProcessing = {
  name: "getReturnForProcessing" as const,
  input: z.object({
    identifier: z.string().min(1),
    locationId: locationIdFormat.schema.optional(),
  }),
  output: z.object({ return: ProcessingReturnData_.nullable() }),
};
